/**
 * Subscription Context
 *
 * Provides subscription status across the app and caches the result
 * to avoid repeated API calls.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import billingApi from '../utils/billingApi';
import { SUBSCRIPTION_REQUIRED_EVENT } from '../utils/axios';

const SubscriptionContext = createContext(null);

/**
 * Hook to access subscription context
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * Subscription Provider Component
 */
export const SubscriptionProvider = ({ children }) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastBlockedPayload, setLastBlockedPayload] = useState(null);

  /**
   * Check if user has an active subscription
   */
  const isActive = useCallback(() => {
    if (!subscription) return false;
    return subscription.has_subscription &&
           ['active', 'trialing'].includes(subscription.status);
  }, [subscription]);

  /**
   * Check if subscription is in a warning state (past_due, etc.)
   */
  const needsAttention = useCallback(() => {
    if (!subscription) return false;
    return subscription.has_subscription &&
           ['past_due', 'unpaid'].includes(subscription.status);
  }, [subscription]);

  /**
   * Fetch subscription status from API
   */
  const refreshSubscription = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.getSubscriptionStatus();
      setSubscription(data);
      setLastBlockedPayload(null);
      return data;
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Failed to load subscription status');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load subscription on mount
  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  useEffect(() => {
    const handleSubscriptionRequired = (event) => {
      setLastBlockedPayload(event.detail ?? null);
      refreshSubscription();
    };

    window.addEventListener(SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
    return () => {
      window.removeEventListener(SUBSCRIPTION_REQUIRED_EVENT, handleSubscriptionRequired);
    };
  }, [refreshSubscription]);

  const value = {
    subscription,
    loading,
    error,
    isActive,
    needsAttention,
    refreshSubscription,
    hasSubscription: subscription?.has_subscription ?? false,
    status: subscription?.status ?? null,
    lastBlockedPayload,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;
